import { noop } from "lodash";
import type { ReactNode } from "react";
import { createContext, useContext, useEffect, useState } from "react";

import {
  useEditMasqueradingSearch,
  useViewMasqueradingSearch,
} from "@/react/components/ViewOnlyMasquerading/masquerading";
import { usePunditUserContext } from "@/react/contexts/punditUserContext";
import {
  getCircleAiMasqueradingSession,
  setCircleAiMasqueradingSession,
} from "@/react/helpers/circleAiMasqueradingSession";
import { useBackendProps } from "@/react/providers/AppContext";

interface MasqueradeContextValue {
  hideBanner: () => void;
  isBannerVisible: boolean;
  isCircleAiAllowed: boolean;
  isCircleAiInputDisabled: boolean;
  isEntityPickerOpen: boolean;
  openEditMasqueradingSearch: () => void;
  openViewMasqueradingSearch: () => void;
  setIsCircleAiAllowed: (isAllowed: boolean) => void;
  setIsEntityPickerOpen: (isOpen: boolean) => void;
  shouldShowCircleAiToggle: boolean;
  showBanner: () => void;
}

const initialState: MasqueradeContextValue = {
  isBannerVisible: false,
  isEntityPickerOpen: false,
  setIsEntityPickerOpen: noop,
  isCircleAiAllowed: false,
  isCircleAiInputDisabled: false,
  shouldShowCircleAiToggle: false,
  setIsCircleAiAllowed: noop,
  showBanner: noop,
  hideBanner: noop,
  openViewMasqueradingSearch: noop,
  openEditMasqueradingSearch: noop,
};
const MasqueradeContext = createContext(initialState);
MasqueradeContext.displayName = "MasqueradeContext";

export const MasqueradeContextProvider = ({
  children,
}: {
  children: ReactNode;
}) => {
  const [isBannerVisible, setIsBannerVisible] = useState(false);
  const [isEntityPickerOpen, setIsEntityPickerOpen] = useState(false);
  const [allowedCommunityMemberId, setAllowedCommunityMemberId] = useState(
    getCircleAiMasqueradingSession,
  );
  const { apiData } = usePunditUserContext();
  const { masquerade_referrer_url: masqueradeReferrerUrl } = useBackendProps();
  // Use the current API response, not the previous member's local-storage cache.
  const currentCommunityMemberId =
    apiData?.current_community_member?.id?.toString() ?? null;
  const shouldShowCircleAiToggle = Boolean(
    masqueradeReferrerUrl &&
    apiData &&
    !apiData.view_only_masquerading &&
    !isBannerVisible &&
    currentCommunityMemberId &&
    apiData.current_community_settings?.circle_ai_masquerading_enabled,
  );
  const isCircleAiAllowed =
    shouldShowCircleAiToggle &&
    allowedCommunityMemberId === currentCommunityMemberId;
  const isCircleAiInputDisabled = Boolean(
    masqueradeReferrerUrl &&
    (!apiData || (shouldShowCircleAiToggle && !isCircleAiAllowed)),
  );

  useEffect(() => {
    // Loading is not a session exit: preserve the preference across reloads.
    if (masqueradeReferrerUrl && !apiData) return;
    if (
      !shouldShowCircleAiToggle ||
      allowedCommunityMemberId !== currentCommunityMemberId
    ) {
      setCircleAiMasqueradingSession(null);
      setAllowedCommunityMemberId(null);
    }
  }, [
    allowedCommunityMemberId,
    apiData,
    currentCommunityMemberId,
    masqueradeReferrerUrl,
    shouldShowCircleAiToggle,
  ]);

  const setIsCircleAiAllowed = (isAllowed: boolean) => {
    const nextCommunityMemberId =
      isAllowed && shouldShowCircleAiToggle ? currentCommunityMemberId : null;
    setCircleAiMasqueradingSession(nextCommunityMemberId);
    setAllowedCommunityMemberId(nextCommunityMemberId);
  };
  const viewMasqueradingSearch = useViewMasqueradingSearch();
  const editMasqueradingSearch = useEditMasqueradingSearch();
  const showBanner = () => {
    setIsBannerVisible(true);
    if (apiData?.current_community_settings?.masquerade_banner_v4_enabled) {
      setIsEntityPickerOpen(true);
    } else {
      void viewMasqueradingSearch.show();
    }
  };
  const hideBanner = () => {
    setIsCircleAiAllowed(false);
    setIsBannerVisible(false);
    setIsEntityPickerOpen(false);
  };
  const openViewMasqueradingSearch = () => void viewMasqueradingSearch.show();
  const openEditMasqueradingSearch = () => void editMasqueradingSearch.show();

  const value = {
    isBannerVisible,
    isEntityPickerOpen,
    setIsEntityPickerOpen,
    isCircleAiAllowed,
    isCircleAiInputDisabled,
    shouldShowCircleAiToggle,
    setIsCircleAiAllowed,
    showBanner,
    hideBanner,
    openViewMasqueradingSearch,
    openEditMasqueradingSearch,
  };

  return (
    <MasqueradeContext.Provider value={value}>
      {children}
    </MasqueradeContext.Provider>
  );
};

export const useMasqueradeContext = () => {
  const context = useContext(MasqueradeContext);
  if (!context) {
    throw new Error(
      "useMasqueradeContext must be used within a MasqueradeContextProvider",
    );
  }
  return context;
};
